<script setup lang="ts">
import { RouteEnum } from '@/constants/enums/RouteEnum';
import { RuleEnum } from '@/constants/enums/RuleEnum';
import type { ThirdPartyLogin } from '@/models/interface/auth';
import { api } from '@/services';
import { isApiSuccess } from '@/utils/api/functions';
import WrongAlert from './errorAlert/WrongAlert.vue';
import WrongMessage from './errorAlert/WrongMessage.vue';
import WrongSignupPhone from './errorAlert/WrongSignupPhone.vue';
import WrongVerificationCode from './errorAlert/WrongVerificationCode.vue';
import FormInput from './formElement/FormInput.vue';

const router = useRouter();
const appStore = useAppStore();
const userStore = useUserStore();
const showPopup = defineModel('showPopup');
const showAlert = defineModel('showAlert');
const information = defineModel<ThirdPartyLogin>('information', { required: true });
const inputRefVerificationCode = ref<InstanceType<typeof FormInput>>();

const { timeLeft, isCounting, startCountdown, resetTime } = useCountdown();

const isSubmit = ref<boolean>(false);

const { fetchData } = useRegister();

const sendVerifyCode = async () => {
  resetTime();
  startCountdown();
  information.value.code = '';

  const res = await api.auth.sendSmsVerify({
    phone: information.value.phone
  });

  if (isApiSuccess(res)) return;

  resetTime();

  if (res.data.errorCode === -4020 || res.data.errorCode === -4038) {
    showPopup.value = true;
    showAlert.value = WrongSignupPhone;
    return;
  }

  showPopup.value = true;
  showAlert.value = WrongMessage;
};

const handleSubmit = async () => {
  isSubmit.value = true;
  const checked = inputRefVerificationCode.value?.handleBlur() ?? false;

  if (!checked) {
    isSubmit.value = false;
    return;
  }

  const res = await api.auth.thirdPartyLogin(information.value);

  if (isApiSuccess(res)) {
    sessionStorage.setItem('phone', `+886${information.value.phone.slice(1)}`);
    userStore.phoneExist = true;
    appStore.isShowNewSMSVerifyPopup = false;
    router.push({ name: RouteEnum.ThankYou });
    return;
  }

  isSubmit.value = false;
  if (res.data.errorCode === -4038 || res.data.errorCode === -4061) {
    showPopup.value = true;
    showAlert.value = WrongSignupPhone;
    return;
  }
  if (res.data.errorCode === -4035 || res.data.errorCode === -4047) {
    showPopup.value = true;
    showAlert.value = WrongVerificationCode;
    return;
  }
  showPopup.value = true;
  showAlert.value = WrongMessage;
};

const handleClick = async () => {
  fetchData({ tel: information.value.phone });

  showPopup.value = true;
  showAlert.value = WrongAlert;
};

onMounted(() => {
  startCountdown();
});
</script>

<template>
  <div class="new-third-part-sms-verification">
    <div class="header-container">
      <div class="header-content">
        <h3>
          立即完成手機驗證<br />
          保障您的帳號安全無虞
        </h3>
      </div>

      <p>立即驗證，解鎖專屬體驗課程</p>
    </div>

    <div class="verify-container">
      <FormInput
        ref="inputRefVerificationCode"
        inputPlaceHolder="請輸入簡訊驗證碼"
        inputTitle="簡訊驗證碼"
        inputType="text"
        :verifyRule="RuleEnum.VerificationCode"
        v-model:inputValue="information.code"
        style="margin: 0"
        canDeleteAll
        :required="true"
      >
        <p class="verify-hint">超過30分鐘，驗證碼即失效</p>
      </FormInput>

      <button
        v-if="information.code.trim() === '' || !isCounting"
        class="send-verify-code"
        :class="{ isCounting: isCounting }"
        @click="sendVerifyCode"
        :disabled="isSubmit"
      >
        {{ isCounting ? `${timeLeft}s` : '重發' }}
      </button>
    </div>

    <div class="submit" :class="{ isSubmit: isSubmit }" @click="handleSubmit">
      <p>送出</p>
      <svg
        v-if="isSubmit"
        width="21"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="animate-spin ml-1.5"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M9.75 1C9.75 0.585786 10.0858 0.25 10.5 0.25C15.8848 0.25 20.25 4.61522 20.25 10C20.25 15.3848 15.8848 19.75 10.5 19.75C5.11522 19.75 0.75 15.3848 0.75 10C0.75 9.58579 1.08579 9.25 1.5 9.25C1.91421 9.25 2.25 9.58579 2.25 10C2.25 14.5564 5.94365 18.25 10.5 18.25C15.0564 18.25 18.75 14.5564 18.75 10C18.75 5.44365 15.0564 1.75 10.5 1.75C10.0858 1.75 9.75 1.41421 9.75 1Z"
          fill="white"
        />
      </svg>
    </div>

    <p class="no-verify" @click="handleClick">一直未收到驗證碼?</p>
  </div>
</template>

<style lang="scss" scoped>
/** style */
.new-third-part-sms-verification {
  width: 100vw;
  height: 100vh;
  background-color: rgb(25, 25, 25);
  padding: 24px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1002;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.header-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  margin-bottom: 16px;

  .header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    h3 {
      font-weight: 600;
      font-size: 64px;
      line-height: 1.4;
      color: white;
      text-align: center;
    }
  }

  p {
    font-weight: 600;
    font-size: 20px;
    line-height: 1.4;
    color: white;
  }
}
.verify-container {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: self-start;
  gap: 8px;
  max-width: 500px;

  .send-verify-code {
    border-radius: 4px;
    margin-top: 20px;
    width: 102px;
    flex-shrink: 0;
    white-space: nowrap;
    height: 48px;
    color: $txt-color-grey-80;
    font-size: 18px;
    background-color: #f4f4f4;
    border: none;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }
  .isCounting {
    user-select: none;
    pointer-events: none;
  }
  .verify-hint {
    color: $txt-color-grey-24;
    font-size: 12px;
    width: 100%;
    padding: 8px 0;
    color: white;
  }
  :deep(.input-title) {
    color: white !important;
  }
}
.submit {
  margin: 16px 0;
  background-color: rgb(253, 58, 149);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: #ffffff;
  height: 40px;
  width: 100%;
  cursor: pointer;
  max-width: 500px;

  &:hover {
    opacity: 0.8;
  }

  &.isSubmit {
    user-select: none;
    pointer-events: none;
  }
}
.no-verify {
  color: white;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-align: center;
  width: 100%;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

.junior-landing-page > .main-content > .card > .submit {
  background-color: $color-brownish-100;
}

.junior-landing-page > .main-content > .card > .no-verify {
  color: $color-brownish-100;
}
</style>
